/**
 * BESTIX v5.0 — CheckoutSuccessPage
 * Order confirmation with e-ticket retrieval
 */

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getETickets, downloadETicketPdf, type ETicketResponse } from '@/services/eventsApi';

interface LocationState {
  orderId?: string;
  mercuryTransactionId?: number;
  totalAmount?: number;
}

export default function CheckoutSuccessPage() {
  const location = useLocation();
  const state = location.state as LocationState | null;
  
  const [tickets, setTickets] = useState<ETicketResponse | null>(null);
  const [ticketError, setTicketError] = useState<string | null>(null);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const orderId = state?.orderId;
  const mercuryTransactionId = state?.mercuryTransactionId;
  const totalAmount = state?.totalAmount;

  // Poll for e-tickets
  useEffect(() => {
    if (!mercuryTransactionId) return;

    const fetchTickets = async () => {
      setIsLoadingTickets(true);
      try {
        const response = await getETickets(mercuryTransactionId);
        setTickets(response);
        setTicketError(null);
      } catch (err) {
        // 404 means tickets not yet available
        if (err instanceof Error && err.message.includes('404')) {
          setTicketError('Tickets are being processed. This may take a few minutes.');
        } else {
          setTicketError(err instanceof Error ? err.message : 'Failed to load tickets');
        }
      } finally {
        setIsLoadingTickets(false);
      }
    };

    fetchTickets();

    // Poll every 30 seconds for up to 5 minutes if tickets not available
    if (!tickets && pollCount < 10) {
      const timer = setTimeout(() => {
        setPollCount(prev => prev + 1);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [mercuryTransactionId, pollCount, tickets]);

  const handleDownloadPdf = () => {
    if (tickets?.type === 'eticket' && tickets.eticket?.pdf_base64) {
      downloadETicketPdf(tickets.eticket.pdf_base64, `tickets-${orderId || 'bestix'}.pdf`);
    }
  };

  // No order info - basic success page
  if (!orderId && !mercuryTransactionId) {
    return (
      <>
        <Helmet><title>Order Confirmed | BESTIX</title></Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center px-4 max-w-md">
            <div className="text-6xl mb-4">🎉</div>
            <h1 className="font-playfair text-3xl font-bold text-navy mb-4">Order Confirmed!</h1>
            <p className="text-gray-600 mb-8">
              Thank you for your purchase. Your tickets will be delivered to your email.
            </p>
            <Link to="/events" className="btn btn-primary">
              Browse More Events
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Order Confirmed | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <Link to="/" className="font-playfair text-2xl font-bold text-navy">BESTIX</Link>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto">
            {/* Success Banner */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="font-playfair text-3xl font-bold text-navy mb-2">Order Confirmed!</h1>
              <p className="text-gray-600">
                Thank you for your purchase. Your order details are below.
              </p>
            </div>

            {/* Order Details */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h2 className="text-lg font-semibold text-navy mb-4">Order Details</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-gray-600">Order ID</dt>
                  <dd className="font-medium text-navy">{orderId}</dd>
                </div>
                {mercuryTransactionId && (
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Transaction ID</dt>
                    <dd className="font-medium text-navy">{mercuryTransactionId}</dd>
                  </div>
                )}
                {totalAmount && (
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Total Paid</dt>
                    <dd className="font-bold text-navy">
                      ${totalAmount.toFixed(2)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* E-Tickets Section */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h2 className="text-lg font-semibold text-navy mb-4">Your Tickets</h2>
              
              {isLoadingTickets && !tickets && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-gold border-t-transparent mr-3" />
                  <span className="text-gray-600">Loading tickets...</span>
                </div>
              )}

              {ticketError && !tickets && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-700">{ticketError}</p>
                  <p className="text-sm text-amber-600 mt-2">
                    You'll receive an email when your tickets are ready.
                  </p>
                </div>
              )}

              {/* E-Ticket PDF */}
              {tickets?.type === 'eticket' && tickets.eticket && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 mb-4">Your e-tickets are ready!</p>
                  <button
                    onClick={handleDownloadPdf}
                    className="btn btn-primary"
                  >
                    Download Tickets (PDF)
                  </button>
                </div>
              )}

              {/* Barcodes */}
              {tickets?.type === 'barcode' && tickets.barcodes && (
                <div className="space-y-4">
                  <p className="text-gray-600 mb-4">Your ticket barcodes:</p>
                  {tickets.barcodes.map((barcode, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Seat {barcode.seat_number}</span>
                        <span className="text-xs text-gray-400">{barcode.barcode_type}</span>
                      </div>
                      <div className="mt-2 font-mono text-lg text-center bg-gray-50 p-3 rounded">
                        {barcode.barcode}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Mobile Transfer */}
              {tickets?.type === 'transfer' && tickets.transfer_urls && (
                <div className="space-y-4">
                  <p className="text-gray-600 mb-4">
                    Your tickets will be transferred to you. Click the links below to accept:
                  </p>
                  {tickets.transfer_urls.map((url, index) => (
                    <a
                      key={index}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center btn btn-primary"
                    >
                      Accept Ticket {index + 1}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">What's Next?</h3>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• A confirmation email has been sent to your email address</li>
                <li>• You can access your tickets from your account anytime</li>
                <li>• Bring your tickets (printed or on mobile) to the venue</li>
                <li>• Arrive early to avoid lines at the entrance</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/account" className="flex-1 btn btn-secondary text-center">
                View My Orders
              </Link>
              <Link to="/events" className="flex-1 btn btn-primary text-center">
                Browse More Events
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
